/**
 * useDocumentUpload
 *
 * Handles document upload (file and URL mode) for Knowledge Base entities.
 */

import { useState, useRef, useCallback } from 'react';
import { useToast } from '../components/ToastProvider';
import api from '../services/api';
import type { EntitySCV2Config } from '../configs/entitySCV2Config';

export interface KBDocumentsTabHandle {
  refresh: () => Promise<void>;
  deleteSelected: () => Promise<string[]>;
  clearSelection: () => void;
}

export interface UseDocumentUploadResult {
  showAddDocModal: boolean;
  setShowAddDocModal: React.Dispatch<React.SetStateAction<boolean>>;
  uploadMode: 'patent' | 'scipub' | 'url' | 'other';
  setUploadMode: React.Dispatch<React.SetStateAction<'patent' | 'scipub' | 'url' | 'other'>>;
  selectedFiles: FileList | null;
  setSelectedFiles: React.Dispatch<React.SetStateAction<FileList | null>>;
  pageUrl: string;
  setPageUrl: React.Dispatch<React.SetStateAction<string>>;
  uploading: boolean;
  kbDocumentsTabRef: React.RefObject<KBDocumentsTabHandle>;
  handleUploadDocuments: (e: React.FormEvent) => Promise<void>;
}

export function useDocumentUpload(
  entityType: string,
  entityId: string | undefined,
  config: EntitySCV2Config | null,
  setEntity: React.Dispatch<React.SetStateAction<unknown>>,
): UseDocumentUploadResult {
  const { addToast } = useToast();
  const kbDocumentsTabRef = useRef<KBDocumentsTabHandle>(null!);

  const [showAddDocModal, setShowAddDocModal] = useState(false);
  const [uploadMode, setUploadMode] = useState<'patent' | 'scipub' | 'url' | 'other'>('patent');
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [pageUrl, setPageUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);

  const handleUploadDocuments = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (entityType !== 'kb' || !entityId) return;

    if (uploadMode !== 'url') {
      if (!selectedFiles) return;

      setUploading(true);
      try {
        const formData = new FormData();
        for (let i = 0; i < selectedFiles.length; i++) {
          formData.append('files', selectedFiles[i]);
        }
        formData.append('document_category', uploadMode);

        const response = await api.post(`/knowledge-bases/${entityId}/documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const result = response.data;
        const hasSuccesses = result.uploaded_count > 0;
        const hasFailures = result.failed_count > 0;

        if (hasSuccesses) {
          const entityData = await config!.service.get(entityId);
          setEntity(entityData);
          await kbDocumentsTabRef.current?.refresh();
          addToast({ type: 'success', title: 'Upload complete', message: `${result.uploaded_count} document(s) uploaded successfully.` });
        }

        if (hasFailures) {
          const failedDetails = result.failed_uploads.map((f: { filename: string; error: string }) => `${f.filename}: ${f.error}`).join('\n');
          addToast({ type: 'error', title: `${result.failed_count} upload(s) failed`, message: failedDetails });
        }

        if (hasSuccesses) {
          setShowAddDocModal(false);
          setSelectedFiles(null);
        }
      } catch (err: unknown) {
        addToast({ type: 'error', title: 'Upload failed', message: (err as Error).message || 'Failed to upload documents' });
      } finally {
        setUploading(false);
      }
    } else {
      // URL mode
      if (!pageUrl || !pageUrl.trim()) {
        addToast({ type: 'warning', title: 'Invalid URL', message: 'Please enter a valid URL' });
        return;
      }

      setUploading(true);
      try {
        const response = await api.post(`/knowledge-bases/${entityId}/documents/url`, { url: pageUrl.trim() });
        const result = response.data;

        if (result.success) {
          setShowAddDocModal(false);
          setPageUrl('');
          const entityData = await config!.service.get(entityId);
          setEntity(entityData);
          await kbDocumentsTabRef.current?.refresh();
          addToast({ type: 'success', title: 'Web page captured', message: `Successfully captured: ${result.document?.title || 'Web page'}` });
        } else {
          addToast({ type: 'error', title: 'Capture failed', message: 'Failed to capture web page' });
        }
      } catch (err: unknown) {
        const e = err as { response?: { data?: { detail?: string } }; message?: string };
        const errorMsg = e.response?.data?.detail || e.message || 'Failed to capture web page';
        addToast({ type: 'error', title: 'Capture failed', message: errorMsg });
      } finally {
        setUploading(false);
      }
    }
  }, [entityType, entityId, uploadMode, selectedFiles, pageUrl, config, setEntity, addToast]);

  return {
    showAddDocModal, setShowAddDocModal,
    uploadMode, setUploadMode,
    selectedFiles, setSelectedFiles,
    pageUrl, setPageUrl,
    uploading,
    kbDocumentsTabRef,
    handleUploadDocuments,
  };
}
